import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, ChapterHead, EASE } from "./shared";

const INITIAL = { name: "", email: "", phone: "", message: "" };

export default function Enquiry() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#1B1915]/25 focus:border-[#1B1915] outline-none py-3.5 text-base text-[#1A201C] placeholder:text-[#1A201C]/35 transition-colors duration-300";

  return (
    <section id="enquire" className="py-20 lg:py-32" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <ChapterHead
            number="10"
            label="Enquire"
            title="Interested in bringing ELYNCOR ELEMENTS into your space?"
            testid="enquiry"
          />
          <Reveal delay={0.15}>
            <p className="mt-8 text-base sm:text-lg text-[#1A201C]/70 leading-relaxed" data-testid="enquiry-subheading">
              Connect with us to learn more about the 7 Chakra Crystal Tree.
            </p>
            <p className="font-hand text-2xl text-[#8C7A56]/80 mt-10">
              we usually reply within a day
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="h-full min-h-[380px] flex flex-col items-center justify-center text-center border border-[#1B1915]/12 bg-[#F4F0E6]/60 rounded-sm px-8 py-16"
                data-testid="enquiry-confirmation"
              >
                <CheckCircle2 className="w-10 h-10 text-[#2C2822] mb-6" strokeWidth={1.5} />
                <p className="font-serif-display text-2xl sm:text-3xl text-[#1B1915] tracking-tight max-w-md leading-snug">
                  Thank you for contacting ELYNCOR ELEMENTS.
                </p>
                <p className="mt-4 text-base text-[#1A201C]/65 max-w-md">
                  We have received your enquiry and will get back to you.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-10 text-[12px] tracking-[0.18em] uppercase text-[#2C2822] underline underline-offset-4 hover:text-[#1B1915] transition-colors duration-300"
                  data-testid="enquiry-send-another-button"
                >
                  Send another enquiry
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="space-y-8"
                data-testid="enquiry-form"
              >
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="enquiry-name" className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/75">
                      Name
                    </label>
                    <input
                      id="enquiry-name"
                      name="name"
                      required
                      value={form.name}
                      onChange={onChange}
                      placeholder="Your full name"
                      className={inputCls}
                      data-testid="enquiry-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="enquiry-email" className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/75">
                      Email
                    </label>
                    <input
                      id="enquiry-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@example.com"
                      className={inputCls}
                      data-testid="enquiry-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/75">
                    Phone / WhatsApp
                  </label>
                  <input
                    id="enquiry-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+91 ..."
                    className={inputCls}
                    data-testid="enquiry-phone-input"
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-message" className="text-[11px] tracking-[0.22em] uppercase text-[#8C7A56]/75">
                    Message
                  </label>
                  <textarea
                    id="enquiry-message"
                    name="message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell us what you'd like to know about the 7 Chakra Gemstone Tree..."
                    className={`${inputCls} resize-none`}
                    data-testid="enquiry-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#1B1915] text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase hover:bg-[#2C2822] disabled:opacity-60 transition-colors duration-300"
                  data-testid="enquiry-submit-button"
                >
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}
